import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../../firebase';
import { signInWithEmailAndPassword, OAuthProvider, signInWithCredential } from 'firebase/auth';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { getDashboardRoute } from '../../utils/roleRedirect';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../msalConfig';
import {
  IconShieldCheck,
  IconClipboardCheck,
  IconCarCrash,
  IconDroplet,
  IconCertificate,
  IconBuilding,
  IconBrandWindows,
  IconArrowRight,
  IconLock,
} from '@tabler/icons-react';

import slide1Img from '../../assets/login-slides/slide-1-checks.jpg';
import slide2Img from '../../assets/login-slides/slide-2-incidents.jpg';
import slide3Img from '../../assets/login-slides/slide-3-fuel.jpg';
import slide4Img from '../../assets/login-slides/slide-4-licences.jpg';
import slide5Img from '../../assets/login-slides/slide-5-overview.jpg';

const slides = [
  {
    icon: IconClipboardCheck,
    title: 'Every check. Every day. Every branch.',
    image: slide1Img,
    body: 'Operators tap through their walkaround in under a minute. Every result logged, timestamped and tied to a named driver — nothing gets missed.',
    stats: [{ val: '96', lbl: 'Checks today' }, { val: '4', lbl: 'Branches' }],
  },
  {
    icon: IconCarCrash,
    title: 'Incidents logged the moment they happen.',
    image: slide2Img,
    body: 'Crash detection arms automatically after every vehicle check. Impact detected, driver prompted, manager notified — all within seconds.',
    stats: [{ val: '2', lbl: 'Incidents today' }, { val: '<30s', lbl: 'Alert time' }],
  },
  {
    icon: IconDroplet,
    title: 'No more paper fuel sheets.',
    image: slide3Img,
    body: 'Staff log meter readings before and after every fill-up. Tank levels update in real time and order emails fire automatically when stocks run low.',
    stats: [{ val: '6,840L', lbl: 'Group fuel' }, { val: '~13d', lbl: 'Remaining' }],
  },
  {
    icon: IconCertificate,
    title: 'MOT, tax and licences — always current.',
    image: slide4Img,
    body: 'Vehicle data pulls straight from the DVLA. Licence records verified via Share My Licence. Reminders fire automatically before anything expires.',
    stats: [{ val: '34', lbl: 'Vehicles' }, { val: '2', lbl: 'Expiring soon' }],
  },
  {
    icon: IconBuilding,
    title: 'The truth about your fleet. All of it.',
    image: slide5Img,
    body: 'One dashboard. Every branch. Check compliance, incidents, fuel levels and driver records — in real time, across the whole group.',
    stats: [{ val: '77%', lbl: 'Compliance' }, { val: '4', lbl: 'Sites live' }],
  },
];

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [msLoading, setMsLoading] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [org, setOrg] = useState(null);
  const navigate = useNavigate();
  const { isAuthenticated, role, onboardingComplete } = useAuth();
  const { instance } = useMsal();

  useEffect(() => {
    if (isAuthenticated && role) {
      navigate(getDashboardRoute(role, onboardingComplete), { replace: true });
    }
  }, [isAuthenticated, role, onboardingComplete, navigate]);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const hostname = window.location.hostname;
    const parts = hostname.split('.');
    if (parts.length >= 3 && parts[0] !== 'localhost' && parts[0] !== 'www') {
      const slug = parts[0];
      const resolveOrg = async () => {
        try {
          const q = query(collection(db, 'organisations'), where('slug', '==', slug));
          const snap = await getDocs(q);
          if (!snap.empty) setOrg({ id: snap.docs[0].id, ...snap.docs[0].data() });
        } catch (err) {
          console.error('Error loading org:', err);
        }
      };
      resolveOrg();
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      switch (err.code) {
        case 'auth/user-not-found':
        case 'auth/wrong-password':
        case 'auth/invalid-credential':
          setError('Incorrect email or password');
          break;
        case 'auth/too-many-requests':
          setError('Too many attempts — try again later');
          break;
        default:
          setError('Something went wrong — please try again');
      }
      setLoading(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    setError('');
    setMsLoading(true);
    try {
      const result = await instance.loginPopup(loginRequest);
      const provider = new OAuthProvider('microsoft.com');
      const credential = provider.credential({
        idToken: result.idToken,
        accessToken: result.accessToken,
      });
      await signInWithCredential(auth, credential);
    } catch (err) {
      console.error('Microsoft login error:', err);
      setError('Microsoft sign in failed — please try again');
      setMsLoading(false);
    }
  };

  const brandColour = org?.brandColour || '#E8610A';
  const slide = slides[currentSlide];
  const SlideIcon = slide.icon;

  return (
    <div style={{
      display: 'flex',
      height: '100vh',
      width: '100vw',
      fontFamily: 'inherit',
      overflow: 'hidden',
    }}>

      {/* Left panel */}
      <div style={{
        width: '40%',
        background: slide.image
          ? `linear-gradient(rgba(0,0,0,0.55), rgba(0,0,0,0.55)), url(${slide.image}) center/cover`
          : brandColour,
        padding: '48px 52px',
        display: 'flex',
        flexDirection: 'column',
        flexShrink: 0,
        overflow: 'hidden',
        transition: 'background 0.3s',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 56 }}>
          <div style={{
            width: 44,
            height: 44,
            borderRadius: 'var(--radius-md)',
            background: 'rgba(255,255,255,0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}>
            <IconShieldCheck size={22} color="white" stroke={1.5} />
          </div>
          <div>
            <div style={{ fontSize: 20, fontWeight: 400, color: '#fff' }}>
              {org?.name || 'Veritas'}
            </div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 2 }}>
              Fleet &amp; equipment compliance
            </div>
          </div>
        </div>

        <div style={{ flex: 1 }}>
          <div style={{
            width: 52,
            height: 52,
            borderRadius: 'var(--radius-lg)',
            background: 'rgba(255,255,255,0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: 24,
          }}>
            <SlideIcon size={26} color="white" stroke={1.5} />
          </div>
          <div style={{
            fontSize: 26,
            fontWeight: 400,
            color: '#fff',
            lineHeight: 1.35,
            marginBottom: 14,
            maxWidth: 400,
          }}>
            {slide.title}
          </div>
          <div style={{
            fontSize: 14,
            color: 'rgba(255,255,255,0.72)',
            lineHeight: 1.7,
            marginBottom: 28,
            maxWidth: 400,
          }}>
            {slide.body}
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            {slide.stats.map((stat, i) => (
              <div key={i} style={{
                background: 'rgba(255,255,255,0.1)',
                borderRadius: 'var(--radius-md)',
                padding: '12px 18px',
              }}>
                <div style={{ fontSize: 22, fontWeight: 400, color: '#fff' }}>{stat.val}</div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)', marginTop: 3 }}>{stat.lbl}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 5, marginTop: 28 }}>
          {slides.map((_, i) => (
            <div
              key={i}
              onClick={() => setCurrentSlide(i)}
              style={{
                width: i === currentSlide ? 16 : 5,
                height: 5,
                borderRadius: 3,
                background: i === currentSlide ? '#fff' : 'rgba(255,255,255,0.3)',
                cursor: 'pointer',
                transition: 'width 0.3s, background 0.3s',
              }}
            />
          ))}
        </div>

        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: 20,
        }}>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>
            An <span style={{ fontWeight: 400, color: 'rgba(255,255,255,0.45)', letterSpacing: '0.04em' }}>AGRIWARE</span> product
          </div>
          <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)' }}>
            &copy; 2026 Agriware Ltd
          </div>
        </div>
      </div>

      {/* Right panel */}
      <div style={{
        flex: 1,
        background: 'var(--color-bg-primary)',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '40px 80px',
        overflow: 'auto',
      }}>
        <div style={{ width: '100%', maxWidth: 480 }}>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            background: 'var(--color-bg-secondary)',
            borderRadius: 'var(--radius-md)',
            padding: '6px 12px',
            marginBottom: 32,
            fontSize: 12,
            fontFamily: 'monospace',
            color: 'var(--color-text-tertiary)',
          }}>
            <IconLock size={12} color="var(--color-success)" stroke={1.5} />
            <span>
              {org ? (
                <><span style={{ color: brandColour, fontWeight: 400 }}>{org.slug}</span>.veritas.agriware.uk/login</>
              ) : (
                <>veritas.agriware.uk/login</>
              )}
            </span>
          </div>

          <h1 style={{ fontSize: 26, fontWeight: 400, marginBottom: 6 }}>Welcome back</h1>
          <p style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginBottom: 12 }}>
            Sign in to {org?.name ? `your ${org.name} account` : 'Veritas'}
          </p>

          {org && (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 5,
              fontSize: 11,
              background: 'var(--color-brand-light)',
              color: 'var(--color-brand)',
              borderRadius: 'var(--radius-full)',
              padding: '3px 10px',
              marginBottom: 28,
            }}>
              <IconBuilding size={12} stroke={1.5} />
              {org.name}
            </div>
          )}

          <form onSubmit={handleLogin}>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
                Email address
              </label>
              <input
                type="email"
                placeholder="name@company.co.uk"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                style={{ width: '100%', boxSizing: 'border-box' }}
              />
            </div>

            <div style={{ marginBottom: 8 }}>
              <label style={{ display: 'block', fontSize: 11, color: 'var(--color-text-secondary)', marginBottom: 6 }}>
                Password
              </label>
              <input
                type="password"
                placeholder="••••••••••••"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                style={{ width: '100%', boxSizing: 'border-box' }}
              />
            </div>

            <div style={{ textAlign: 'right', marginBottom: 20 }}>
              <span
                onClick={() => navigate('/reset-password')}
                style={{ fontSize: 12, color: brandColour, cursor: 'pointer' }}
              >
                Forgot password?
              </span>
            </div>

            {error && (
              <div style={{
                fontSize: 12,
                color: 'var(--color-danger)',
                background: 'var(--color-danger-light)',
                borderRadius: 'var(--radius-md)',
                padding: '10px 14px',
                marginBottom: 16,
              }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                background: brandColour,
                color: '#fff',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                padding: '12px',
                fontSize: 14,
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                marginBottom: 16,
                transition: 'background 0.3s',
              }}
            >
              {loading ? 'Signing in...' : 'Sign in'}
              {!loading && <IconArrowRight size={16} stroke={1.5} />}
            </button>
          </form>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
            <div style={{ flex: 1, height: '0.5px', background: 'var(--color-border-light)' }} />
            <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>or</span>
            <div style={{ flex: 1, height: '0.5px', background: 'var(--color-border-light)' }} />
          </div>

          <button
            onClick={handleMicrosoftLogin}
            disabled={msLoading}
            style={{
              width: '100%',
              background: 'var(--color-bg-primary)',
              color: 'var(--color-text-primary)',
              border: '0.5px solid var(--color-border-medium)',
              borderRadius: 'var(--radius-md)',
              padding: '11px',
              fontSize: 13,
              cursor: msLoading ? 'not-allowed' : 'pointer',
              opacity: msLoading ? 0.7 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8,
              marginBottom: 24,
            }}
          >
            <div style={{
              width: 16,
              height: 16,
              background: '#0078D4',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <IconBrandWindows size={10} color="white" stroke={1.5} />
            </div>
            {msLoading ? 'Signing in with Microsoft...' : 'Sign in with Microsoft'}
          </button>

          <div style={{ fontSize: 12, color: 'var(--color-text-tertiary)', textAlign: 'center', marginBottom: 14 }}>
            Having trouble?{' '}
            <span style={{ color: brandColour, cursor: 'pointer' }}>
              Contact your administrator
            </span>
          </div>

          <div style={{
            fontSize: 11,
            color: 'var(--color-text-tertiary)',
            textAlign: 'center',
            paddingTop: 16,
            borderTop: '0.5px solid var(--color-border-light)',
          }}>
            Veritas is a product by{' '}
            <span style={{ color: 'var(--color-text-secondary)', fontWeight: 400 }}>Agriware</span>
            {' · '}
            <span style={{ cursor: 'pointer' }}>agriware.uk</span>
          </div>

        </div>
      </div>
    </div>
  );
}